/**
 * Hiring-demand board — restructure mode.
 *
 * WHY A MODE AND NOT ALWAYS-ON CONTROLS. Moving a whole team between units is a
 * rare, consequential act next to the everyday one of adding a role, and a
 * control for it sitting permanently on every team header would be the most
 * dangerous thing on the board by a distance. Turning it on is the confirmation
 * step; there is no second one, because every move is recorded as a delta and
 * removing it puts the row straight back (see `placements` in taDemand.js).
 *
 * THREE THINGS MOVE, and the board treats them as one question — what moved,
 * where to:
 *
 *   a team    to another unit, carrying every role and person in it
 *   a person  to another team, or another unit entirely
 *   a role    to another team in its own unit (the drag that already existed)
 *
 * Nothing is checked against the org chart on the way in. A reorg is precisely
 * the case where the chart is behind, and refusing a unit it has not caught up
 * with would put a transcription between a lead and their own organisation.
 */
import { useState } from 'react';
import { Loader2, Shuffle, X } from 'lucide-react';
import type { DeptNode } from './demandModel';
import { isRealUnit } from './demandModel';
import { selectCls } from './demandUi';

/** Every unit somebody could be moved into, as `Department › Unit`. */
export interface UnitChoice {
  department: string;
  unit: string;
  label: string;
  teams: string[];
}

export function unitChoices(tree: DeptNode[]): UnitChoice[] {
  return tree
    .filter((d) => d.key !== '__unassigned__')
    .flatMap((d) =>
      d.units.filter(isRealUnit).map((u) => ({
        department: d.name,
        unit: u.name,
        label: `${d.name} › ${u.name}`,
        teams: u.teams.map((t) => t.name),
      })),
    );
}

export function RestructureToggle({ on, onChange }: { on: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!on)}
      aria-pressed={on}
      title="Move a whole team to another unit, or a colleague to another team — for a reorg, not for everyday edits"
      className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs font-semibold ${
        on
          ? 'border-violet-600 bg-violet-600 text-white hover:bg-violet-700'
          : 'border-gray-300 text-gray-700 hover:bg-gray-50'
      }`}
    >
      <Shuffle className="h-3 w-3" />
      {on ? 'Restructuring' : 'Restructure'}
    </button>
  );
}

/**
 * Move one thing somewhere else.
 *
 * Closed it is a word; open it is a unit picker and, once a unit is chosen, that
 * unit's own teams. The two are separate steps rather than one flat list of
 * every unit × team in the company, which would be several hundred options and
 * unreadable — and the unit is the decision that matters, the team being a
 * refinement of it.
 */
export function MoveTo({
  what,
  units,
  current,
  busy,
  teamsOnly = false,
  onMove,
}: {
  /** What is being moved, for the label — "Culture & Community", "Zohal Mir". */
  what: string;
  units: UnitChoice[];
  current: { department: string; unit: string; team?: string };
  busy: boolean;
  /** A TEAM moves between units and has no team of its own to choose. */
  teamsOnly?: boolean;
  onMove: (to: { department: string; unit: string; team?: string }) => void;
}) {
  const [open, setOpen] = useState(false);
  const [unitKey, setUnitKey] = useState(`${current.department}|${current.unit}`);
  const [teamKey, setTeamKey] = useState(current.team ?? '');

  if (!open) {
    return (
      <button
        type="button"
        // Named, because a board in restructure mode has one of these on every
        // team header and every person — "Move" alone identifies nothing.
        aria-label={`Move ${what}`}
        onClick={() => {
          setUnitKey(`${current.department}|${current.unit}`);
          setTeamKey(current.team ?? '');
          setOpen(true);
        }}
        disabled={busy}
        className="inline-flex h-[22px] shrink-0 items-center gap-1 rounded-sm border border-violet-300 px-1.5 text-[11px] font-semibold text-violet-700 hover:bg-violet-50 disabled:opacity-60"
      >
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Shuffle className="h-3 w-3" />}
        Move
      </button>
    );
  }

  const chosen = units.find((u) => `${u.department}|${u.unit}` === unitKey);
  const commit = () => {
    if (!chosen) return;
    onMove(
      teamsOnly
        ? { department: chosen.department, unit: chosen.unit }
        : { department: chosen.department, unit: chosen.unit, team: teamKey },
    );
    setOpen(false);
  };

  return (
    <span className="flex shrink-0 items-center gap-1">
      <select
        autoFocus
        className={`${selectCls} max-w-[220px]`}
        value={unitKey}
        aria-label={`Move ${what} to which unit`}
        onChange={(e) => {
          setUnitKey(e.target.value);
          // The team list belongs to the unit, so a unit change invalidates it.
          // Landing on "no team" is the honest default: whether this person
          // belongs to one of the new unit's teams is the next decision, not
          // something to carry over from the unit they left.
          setTeamKey('');
        }}
      >
        {units.map((u) => (
          <option key={`${u.department}|${u.unit}`} value={`${u.department}|${u.unit}`}>
            {u.label}
          </option>
        ))}
      </select>
      {!teamsOnly && (
        <select
          className={`${selectCls} max-w-[160px]`}
          aria-label={`Move ${what} to which team`}
          value={teamKey}
          onChange={(e) => setTeamKey(e.target.value)}
        >
          {/* Blank is a real choice: somebody can belong to a unit and no team. */}
          <option value="">No team</option>
          {(chosen?.teams ?? []).map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      )}
      {/*
        ⚠️ AN EXPLICIT BUTTON, for both kinds. Committing on the team select's
        `change` made two reachable moves impossible to express: "another unit,
        no team" fires no change because the select already holds '', and so does
        re-picking a team whose value survived the unit switch. A select's
        `change` is not a decision — this is.
      */}
      <button
        type="button"
        aria-label={`Move ${what} to the chosen unit`}
        onClick={commit}
        className="h-[22px] shrink-0 rounded-sm bg-violet-600 px-2 text-[11px] font-semibold text-white hover:bg-violet-700"
      >
        Move
      </button>
      <button
        type="button"
        /* mousedown so the click lands before a select's blur, AND click so the
           keyboard works — Enter on a button dispatches click, never mousedown,
           so a mousedown-only cancel simply did nothing from the keyboard. */
        onMouseDown={(e) => {
          e.preventDefault();
          setOpen(false);
        }}
        onClick={() => setOpen(false)}
        aria-label="Cancel the move"
        className="grid h-[22px] w-[22px] shrink-0 place-items-center rounded-sm text-gray-400 hover:bg-gray-100"
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  );
}

/** Marks a row the board is showing somewhere other than its source system says. */
export function MovedTag({
  movedWithTeam,
  placedBy,
  onUndo,
}: {
  movedWithTeam?: string;
  placedBy?: string;
  onUndo?: () => void;
}) {
  if (!movedWithTeam && !placedBy) return null;
  return (
    <span
      className="group/moved inline-flex shrink-0 items-center gap-1 rounded-sm bg-violet-50 px-1 text-[10px] font-semibold leading-4 text-violet-700"
      title={
        movedWithTeam
          ? `Moved with its team: ${movedWithTeam}. The HDB and the people database still say otherwise.`
          : `Moved here by ${placedBy}. The HDB and the people database still say otherwise.`
      }
    >
      moved
      {onUndo && (
        <button
          type="button"
          onClick={onUndo}
          aria-label="Undo this move"
          className="opacity-0 hover:text-violet-900 focus:opacity-100 group-hover/moved:opacity-100"
        >
          <X className="h-2.5 w-2.5" />
        </button>
      )}
    </span>
  );
}
